import React, { useState, useEffect, useCallback } from "react";
import MainLayout from "../layouts/MainLayout";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  Phone,
  ArrowRight,
  MapPin,
  ShieldCheck,
  ArrowLeft,
  RefreshCw,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { signInWithGooglePopup } from "../lib/googleAuth";
import { useAuth } from "../contexts/AuthContext";
import { authService } from "../services/authService";

const CDN = process.env.REACT_APP_CDN_URL;
const REGISTER_BG =
  CDN?.trim()
    ? `${CDN.replace(/\/$/, "")}/site/auth/register.jpg`
    : "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1400&q=80";

const Register = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState("form");
  const [otp, setOtp] = useState("");
  const [resendSec, setResendSec] = useState(0);
  const [otpExpiresMin, setOtpExpiresMin] = useState(10);

  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
    agree: false,
  });

  useEffect(() => {
    if (resendSec <= 0) return;
    const t = setInterval(() => setResendSec((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendSec]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const sendOtpRequest = useCallback(async () => {
    const data = await authService.sendRegisterOtp({
      fullName: formData.fullName.trim(),
      email: formData.email.trim(),
      password: formData.password,
    });
    if (typeof data.expiresInMinutes === "number") {
      setOtpExpiresMin(data.expiresInMinutes);
    }
    setResendSec(60);
    return data;
  }, [formData.fullName, formData.email, formData.password]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError("");

    if (formData.password !== formData.confirmPassword) {
      setError("Mật khẩu xác nhận không khớp.");
      return;
    }

    if (!formData.agree) {
      setError("Bạn cần đồng ý với điều khoản sử dụng.");
      return;
    }

    setSubmitting(true);
    try {
      await sendOtpRequest();
      setStep("otp");
      setOtp("");
    } catch (err) {
      setError(err.message || "Đã có lỗi xảy ra.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    const code = otp.replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) {
      setError("Vui lòng nhập đủ 6 chữ số mã OTP.");
      return;
    }

    setSubmitting(true);
    try {
      const data = await authService.verifyRegisterOtp({
        email: formData.email.trim(),
        otp: code,
      });
      login({ token: data.token, refreshToken: data.refreshToken, user: data.user });
      navigate("/");
    } catch (err) {
      setError(err.message || "Mã OTP không hợp lệ.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendSec > 0 || submitting) return;
    setError("");
    setSubmitting(true);
    try {
      await sendOtpRequest();
    } catch (err) {
      setError(err.message || "Không gửi lại được mã.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleRegister = async () => {
    setError("");
    setGoogleLoading(true);

    try {
      const { credential: idToken } = await signInWithGooglePopup();
      const data = await authService.googleLogin({ idToken });
      login({ token: data.token, refreshToken: data.refreshToken, user: data.user });
      if (data.user?.role === "admin") {
        navigate("/admin/dashboard", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      setError(err.message || "Đăng ký bằng Google chưa thành công.");
    } finally {
      setGoogleLoading(false);
    }
  };

  const maskEmail = (em) => {
    const [u, d] = em.split("@");
    if (!d) return em;
    const show = u.slice(0, 2);
    return `${show}•••@${d}`;
  };

  return (
    <MainLayout hideFooter>
      <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden bg-[#f6efe4] px-2 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:px-3 sm:py-2.5 md:px-4">
        <div className="mx-auto flex h-full min-h-0 w-full max-w-7xl flex-1 flex-col">
          <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden rounded-[24px] border border-[#d9ccb8] bg-[#fffaf1] shadow-[0_18px_48px_rgba(70,50,30,0.10)] sm:rounded-[28px]">
            <div className="grid h-full min-h-0 flex-1 grid-cols-1 lg:grid-cols-2 lg:grid-rows-1">
              <div className="relative hidden min-h-0 lg:block">
                <img
                  src={REGISTER_BG}
                  alt="Đà Lạt"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(57,73,55,0.30)_0%,rgba(48,60,45,0.72)_58%,rgba(35,43,33,0.86)_100%)]" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,240,212,0.24),transparent_42%)]" />

                <div className="relative z-10 flex h-full min-h-0 flex-col justify-between gap-3 p-5 text-white xl:p-7">
                  <div className="min-w-0">
                    <div className="mb-3 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-white/20 bg-white/12 backdrop-blur-sm sm:h-12 sm:w-12">
                      <MapPin size={20} />
                    </div>

                    <p className="mb-2 text-[0.65rem] uppercase tracking-[0.24em] text-white/75">
                      Lòng Vòng Đà Lạt
                    </p>

                    <h1 className="font-faustina text-3xl font-bold italic leading-tight sm:text-4xl xl:text-5xl">
                      Tạo tài khoản
                      <br />
                      khám phá
                    </h1>

                    <p className="mt-4 max-w-md text-sm leading-7 text-white/86 xl:text-[15px]">
                      Lưu điểm đến, cá nhân hóa lịch trình và kết nối cùng những trải nghiệm đẹp
                      nhất tại thành phố sương mù.
                    </p>
                  </div>

                  <div className="shrink-0 rounded-[20px] border border-[#f1dfbf]/20 bg-black/20 p-4 backdrop-blur-md">
                    <p className="text-sm italic leading-7 text-white/90">
                      “Một tài khoản mới cho một hành trình nhiều cảm hứng hơn.”
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto lg:max-h-full">
                <div className="mx-auto flex h-full min-h-0 w-full max-w-xl flex-1 flex-col gap-3 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5 md:px-6 md:py-6">
                  <div className="shrink-0">
                    <p className="mb-1 text-[0.68rem] font-bold uppercase tracking-[0.18em] text-[#7f745e]">
                      {step === "form" ? "Create account" : "Verify email"}
                    </p>

                    <h2 className="font-faustina text-3xl font-bold tracking-tight text-[#2f352c] sm:text-4xl">
                      {step === "form" ? "Đăng ký" : "Xác minh email"}
                    </h2>

                    <p
                      className={
                        step === "form"
                          ? "mt-2 text-sm leading-7 text-[#5f675b] [@media(max-height:620px)]:hidden"
                          : "mt-2 text-sm leading-7 text-[#5f675b]"
                      }
                    >
                      {step === "form"
                        ? "Tạo tài khoản mới — chúng tôi sẽ gửi mã OTP tới email của bạn."
                        : `Nhập mã 6 chữ số đã gửi tới ${maskEmail(formData.email.trim())}. Mã có hiệu lực khoảng ${otpExpiresMin} phút.`}
                    </p>
                  </div>

                  {step === "form" ? (
                    <div className="flex min-h-0 flex-1 flex-col gap-3 [@media(max-height:620px)]:gap-2">
                      <form
                        onSubmit={handleSendOtp}
                        className="grid min-h-0 flex-1 auto-rows-min grid-cols-1 content-start gap-x-3 gap-y-3 sm:grid-cols-2 [@media(max-height:620px)]:gap-y-2"
                      >
                        <div className="min-w-0 sm:col-span-1">
                          <label className="mb-1.5 block text-xs font-extrabold uppercase tracking-[0.12em] text-[#6c6558]">
                            Họ và tên
                          </label>
                          <div className="flex min-w-0 items-center gap-2 rounded-[18px] border border-[#d9ccb8] bg-[#fffdf8] px-3 py-3 shadow-[0_8px_18px_rgba(70,50,30,0.05)] transition focus-within:border-[#6a7d62] focus-within:ring-2 focus-within:ring-[rgba(95,116,95,0.18)]">
                            <User size={17} className="shrink-0 text-[#7d776a]" />
                            <input
                              type="text"
                              name="fullName"
                              value={formData.fullName}
                              onChange={handleChange}
                              placeholder="Họ và tên"
                              className="min-w-0 flex-1 bg-transparent text-sm text-[#2f352c] outline-none placeholder:text-[#948c7d]"
                              required
                            />
                          </div>
                        </div>

                        <div className="min-w-0 sm:col-span-1">
                          <label className="mb-1.5 block text-xs font-extrabold uppercase tracking-[0.12em] text-[#6c6558]">
                            Điện thoại
                          </label>
                          <div className="flex min-w-0 items-center gap-2 rounded-[18px] border border-[#d9ccb8] bg-[#fffdf8] px-3 py-3 shadow-[0_8px_18px_rgba(70,50,30,0.05)] transition focus-within:border-[#6a7d62] focus-within:ring-2 focus-within:ring-[rgba(95,116,95,0.18)]">
                            <Phone size={17} className="shrink-0 text-[#7d776a]" />
                            <input
                              type="text"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              placeholder="Tuỳ chọn"
                              className="min-w-0 flex-1 bg-transparent text-sm text-[#2f352c] outline-none placeholder:text-[#948c7d]"
                            />
                          </div>
                        </div>

                        <div className="min-w-0 sm:col-span-2">
                          <label className="mb-1.5 block text-xs font-extrabold uppercase tracking-[0.12em] text-[#6c6558]">
                            Email
                          </label>
                          <div className="flex min-w-0 items-center gap-2 rounded-[18px] border border-[#d9ccb8] bg-[#fffdf8] px-3 py-3 shadow-[0_8px_18px_rgba(70,50,30,0.05)] transition focus-within:border-[#6a7d62] focus-within:ring-2 focus-within:ring-[rgba(95,116,95,0.18)]">
                            <Mail size={17} className="shrink-0 text-[#7d776a]" />
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              placeholder="Email"
                              className="min-w-0 flex-1 bg-transparent text-sm text-[#2f352c] outline-none placeholder:text-[#948c7d]"
                              required
                            />
                          </div>
                        </div>

                        <div className="min-w-0 sm:col-span-1">
                          <label className="mb-1.5 block text-xs font-extrabold uppercase tracking-[0.12em] text-[#6c6558]">
                            Mật khẩu
                          </label>
                          <div className="flex min-w-0 items-center gap-2 rounded-[18px] border border-[#d9ccb8] bg-[#fffdf8] px-3 py-3 shadow-[0_8px_18px_rgba(70,50,30,0.05)] transition focus-within:border-[#6a7d62] focus-within:ring-2 focus-within:ring-[rgba(95,116,95,0.18)]">
                            <Lock size={17} className="shrink-0 text-[#7d776a]" />
                            <input
                              type={showPassword ? "text" : "password"}
                              name="password"
                              value={formData.password}
                              onChange={handleChange}
                              placeholder="≥ 8 ký tự"
                              className="min-w-0 flex-1 bg-transparent text-sm text-[#2f352c] outline-none placeholder:text-[#948c7d]"
                              required
                              minLength={8}
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword((prev) => !prev)}
                              className="shrink-0 text-[#7d776a] transition hover:text-[#5f745f]"
                            >
                              {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                            </button>
                          </div>
                        </div>

                        <div className="min-w-0 sm:col-span-1">
                          <label className="mb-1.5 block text-xs font-extrabold uppercase tracking-[0.12em] text-[#6c6558]">
                            Xác nhận mật khẩu
                          </label>
                          <div className="flex min-w-0 items-center gap-2 rounded-[18px] border border-[#d9ccb8] bg-[#fffdf8] px-3 py-3 shadow-[0_8px_18px_rgba(70,50,30,0.05)] transition focus-within:border-[#6a7d62] focus-within:ring-2 focus-within:ring-[rgba(95,116,95,0.18)]">
                            <Lock size={17} className="shrink-0 text-[#7d776a]" />
                            <input
                              type={showConfirmPassword ? "text" : "password"}
                              name="confirmPassword"
                              value={formData.confirmPassword}
                              onChange={handleChange}
                              placeholder="Nhập lại mật khẩu"
                              className="min-w-0 flex-1 bg-transparent text-sm text-[#2f352c] outline-none placeholder:text-[#948c7d]"
                              required
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmPassword((prev) => !prev)}
                              className="shrink-0 text-[#7d776a] transition hover:text-[#5f745f]"
                            >
                              {showConfirmPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                            </button>
                          </div>
                        </div>

                        <label className="flex min-w-0 items-start gap-2 rounded-[18px] border border-[#d9ccb8] bg-[#f7f0e4] px-3 py-3 text-xs font-medium leading-6 text-[#4f5748] sm:col-span-2 sm:items-center">
                          <input
                            type="checkbox"
                            name="agree"
                            checked={formData.agree}
                            onChange={handleChange}
                            className="mt-0.5 shrink-0 accent-[#5f745f] sm:mt-0"
                          />
                          <span>Đồng ý điều khoản & chính sách bảo mật.</span>
                        </label>

                        {error ? (
                          <div className="rounded-[18px] border border-[#efc9c9] bg-[#fff3f3] px-3 py-2 text-sm font-medium text-[#a14848] sm:col-span-2">
                            {error}
                          </div>
                        ) : null}

                        <button
                          type="submit"
                          disabled={submitting}
                          className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-full bg-[#5f745f] px-5 py-3 text-sm font-bold text-white shadow-[0_12px_24px_rgba(95,116,95,0.28)] ring-1 ring-black/10 transition hover:bg-[#4f6250] disabled:opacity-60 sm:col-span-2 sm:text-base"
                        >
                          {submitting ? "Đang gửi mã..." : "Gửi mã OTP"}
                          <ArrowRight size={16} />
                        </button>
                      </form>

                      <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                        <div className="h-px flex-1 bg-[#ddd1bf]" />
                        <span className="shrink-0 text-[0.7rem] font-semibold uppercase tracking-[0.1em] text-[#7e7668] sm:text-xs">
                          hoặc
                        </span>
                        <div className="h-px flex-1 bg-[#ddd1bf]" />
                      </div>

                      <button
                        type="button"
                        onClick={handleGoogleRegister}
                        disabled={googleLoading}
                        className="inline-flex w-full shrink-0 min-w-0 items-center justify-center gap-2 rounded-lg border border-brand-border bg-[#fffdf8] px-3 py-2.5 text-xs font-semibold text-text-main shadow-soft transition hover:border-accent/60 hover:bg-bg-section/70 hover:shadow-soft-md disabled:opacity-60 sm:rounded-xl sm:text-sm"
                      >
                        <img
                          src="https://www.svgrepo.com/show/475656/google-color.svg"
                          alt="Google"
                          className="h-4 w-4 sm:h-5 sm:w-5"
                        />
                        {googleLoading ? "Đang xử lý..." : "Đăng ký với Google"}
                      </button>
                    </div>
                  ) : (
                    <form
                      onSubmit={handleVerifyOtp}
                      className="flex min-h-0 flex-1 flex-col gap-3 [@media(max-height:620px)]:gap-2"
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setStep("form");
                          setOtp("");
                          setError("");
                        }}
                        className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-[#5f675b] transition hover:text-[#5f745f]"
                      >
                        <ArrowLeft size={14} />
                        Quay lại chỉnh sửa
                      </button>

                      <div className="flex shrink-0 justify-center py-1">
                        <div className="flex h-14 w-14 items-center justify-center rounded-[20px] border border-[#d9ccb8] bg-[#f2eadb] text-[#5f745f] shadow-[0_8px_18px_rgba(70,50,30,0.06)]">
                          <ShieldCheck size={26} />
                        </div>
                      </div>

                      <div className="rounded-[24px] border border-[#d7cab6] bg-[#5f745f] px-4 py-5 shadow-[0_14px_28px_rgba(95,116,95,0.24)]">
                        <label className="mb-2 block text-center text-xs font-extrabold uppercase tracking-[0.14em] text-white/70 sm:text-sm">
                          Mã OTP (6 số)
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={6}
                          value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                          placeholder="••••••"
                          className="w-full min-w-0 rounded-[18px] border border-white/15 bg-white/10 py-3 text-center font-mono text-xl tracking-[0.28em] text-white shadow-sm outline-none transition placeholder:text-white/45 focus:border-white/30 focus:ring-2 focus:ring-white/20 sm:text-2xl sm:tracking-[0.35em]"
                        />
                        <p className="mt-3 text-center text-xs leading-6 text-white/75">
                          Mã xác minh đã được gửi đến email của bạn.
                        </p>
                      </div>

                      {error ? (
                        <div className="shrink-0 rounded-[18px] border border-[#efc9c9] bg-[#fff3f3] px-3 py-2 text-sm font-medium text-[#a14848]">
                          {error}
                        </div>
                      ) : null}

                      <button
                        type="submit"
                        disabled={submitting}
                        className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-full bg-[#5f745f] px-5 py-3 text-sm font-bold text-white shadow-[0_12px_24px_rgba(95,116,95,0.28)] ring-1 ring-black/10 transition hover:bg-[#4f6250] disabled:opacity-60 sm:text-base"
                      >
                        {submitting ? "Đang xác minh..." : "Xác minh & hoàn tất"}
                        <ArrowRight size={16} />
                      </button>

                      <div className="shrink-0 text-center">
                        <button
                          type="button"
                          onClick={handleResendOtp}
                          disabled={resendSec > 0 || submitting}
                          className="inline-flex items-center gap-1.5 text-sm font-bold text-[#5f745f] underline-offset-2 hover:text-[#4f6250] hover:underline disabled:text-[#9b9588]"
                        >
                          <RefreshCw size={13} />
                          {resendSec > 0 ? `Gửi lại mã (${resendSec}s)` : "Gửi lại mã OTP"}
                        </button>
                      </div>
                    </form>
                  )}

                  <p className="shrink-0 pt-1 text-center text-sm font-medium text-[#5f675b] sm:pt-2">
                    Đã có tài khoản?{" "}
                    <Link
                      to="/login"
                      className="font-bold text-[#5f745f] underline-offset-2 hover:text-[#4f6250] hover:underline"
                    >
                      Đăng nhập
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Register;